/**
 * AuthentikBridge — generisches OIDC (oidc-client-ts) gegen eine
 * Authentik-Instanz. Zweite Auth-Version des Testers (VITE_AUTH_MODE=authentik):
 * bestehende Kundeninstanzen bleiben auf der LogtoBridge, neue Projekte der
 * Produktionslinie nutzen diese Bridge (Backend-Gegenstueck: OIDC_ISSUER_URL).
 *
 * Verdrahtung identisch zur LogtoBridge: Token-Provider + Logout-Handler im
 * Auth-Store registrieren, Claims (sub/name/email/roles) in den Store, nicht
 * angemeldete Benutzer werden zum Login umgeleitet. Rollen kommen bei
 * Authentik aus dem groups-Claim (Gruppen "tester:reviewer" usw.) — ein
 * roles-Claim (Property-Mapping) wird bevorzugt, falls vorhanden.
 */

import { useEffect, useState, type ReactNode } from "react"
import { UserManager, WebStorageStateStore, type User } from "oidc-client-ts"
import { useLocation } from "react-router-dom"
import { RefreshTokenImSpeicherStore } from "@/lib/refreshTokenStore"
import { setLogoutHandler, setTokenProvider, useAuthStore } from "@/store/auth"

const OIDC_ISSUER = import.meta.env.VITE_OIDC_ISSUER ?? ""
const OIDC_CLIENT_ID = import.meta.env.VITE_OIDC_CLIENT_ID ?? ""

// Redirect: <origin>/tester/callback — gleiche Konvention wie die LogtoBridge
const callbackUrl = () => new URL(import.meta.env.BASE_URL + "callback", window.location.origin).href
const startUrl = () => new URL(import.meta.env.BASE_URL, window.location.origin).href

// Singleton — CallbackPage braucht dieselbe Instanz fuer signinRedirectCallback
let _userManager: UserManager | null = null

// Waehrend des Abmeldens den Auto-Login-Redirect unterdruecken. Sonst Race:
// removeUser() feuert userUnloaded -> user wird null -> der "nicht angemeldet"-
// Effect ruft signinRedirect(), der neue Authorize-Flow CANCELT den laufenden
// Invalidation-Flow (beobachtet: /flows/-/cancel/ im Authentik-Log) und die
// noch lebende Session loggt einen sofort wieder ein. Beobachtet 26.07.2026
// auf kiwerft; dieselbe Ursache hinter dem Lehner-Symptom "sofort wieder drin".
let _logoutLaeuft = false

export function getUserManager(): UserManager {
  if (!_userManager) {
    _userManager = new UserManager({
      authority: OIDC_ISSUER,
      client_id: OIDC_CLIENT_ID,
      redirect_uri: callbackUrl(),
      post_logout_redirect_uri: startUrl(),
      // offline_access → Refresh-Token: Authentik-Access-Tokens leben nur
      // Minuten; ohne Refresh-Token gilt die Session nach Ablauf als weg und
      // JEDER Seitenaufruf bounced durch den Authentik-Redirect ("muss sich
      // ständig neu anmelden"). Der Provider in Authentik muss den Scope
      // offline_access erlauben (Erweiterte Protokolleinstellungen ▸ Scopes).
      scope: "openid profile email offline_access",
      // #59: Refresh-Token NICHT in den localStorage (same-origin mit der
      // Kunden-App → für jede App-XSS lesbar). Access-Token/Profil bleiben
      // persistiert, der Refresh-Token nur im Speicher dieses Tabs.
      userStore: new RefreshTokenImSpeicherStore(
        new WebStorageStateStore({ store: window.localStorage })
      ),
      automaticSilentRenew: true,
    })
  }
  return _userManager
}

/** Abgelaufene Session per Refresh-Token still erneuern (null bei Misserfolg). */
async function stillErneuern(um: UserManager): Promise<User | null> {
  try {
    const u = await um.signinSilent()
    return u && !u.expired ? u : null
  } catch {
    return null
  }
}

function rollenAusClaims(profile: Record<string, unknown>): string[] {
  const quelle = profile.roles ?? profile.groups
  return Array.isArray(quelle) ? quelle.map(String) : []
}

export function AuthentikBridge({ children }: { children: ReactNode }) {
  const setUser = useAuthStore((s) => s.setUser)
  const storeUser = useAuthStore((s) => s.user)
  const [user, setOidcUser] = useState<User | null>(null)
  const [geladen, setGeladen] = useState(false)
  const istCallback = useLocation().pathname === "/callback"

  // Token-Provider: Access-Token aus dem UserManager (Muster LogtoBridge);
  // abgelaufen + Refresh-Token vorhanden → still erneuern statt null (haelt
  // API-Aufrufe und das Terminal-WebSocket ueber die Token-Laufzeit hinaus).
  useEffect(() => {
    const um = getUserManager()
    setTokenProvider(async () => {
      const u = await um.getUser()
      if (u && !u.expired) return u.access_token
      if (u?.refresh_token) {
        const neu = await stillErneuern(um)
        if (neu) return neu.access_token
      }
      return null
    })
    return () => setTokenProvider(null)
  }, [])

  // Abmelden-Handler (TopBar) — beendet lokale UND zentrale Session.
  //
  // Verifiziert am 25.07.2026 mit eigenem Testkonto: NUR die Kombination wirkt.
  // Der lokale Token-Store hält die App-Session am Leben (auch ohne SSO),
  // und die zentrale Authentik-Session muss über
  // `/flows/-/default/invalidation/` beendet werden (antwortet 302, startet
  // den Flow) — `/if/flow/<slug>/` liefert nur die Oberfläche (200) und
  // beendet nichts. signoutRedirect() scheitert hier, weil der Provider mit
  // matching_mode "strict" ausschliesslich die Callback-URL erlaubt und jede
  // post_logout_redirect_uri ablehnt.
  useEffect(() => {
    const um = getUserManager()
    setLogoutHandler(async () => {
      _logoutLaeuft = true
      try {
        await um.removeUser()
      } catch {
        /* Store ggf. schon leer */
      }
      const idpOrigin = new URL(OIDC_ISSUER).origin
      window.location.href = `${idpOrigin}/flows/-/default/invalidation/`
    })
    return () => setLogoutHandler(null)
  }, [])

  // Session laden; User-Events (Login/Renew/Logout) in den lokalen State
  useEffect(() => {
    const um = getUserManager()
    const onLoaded = (u: User) => setOidcUser(u)
    const onUnloaded = () => setOidcUser(null)
    // Access-Token abgelaufen: mit Refresh-Token (im Speicher) still
    // erneuern; ohne (z. B. nach Reload, #59) → user = null → der Effect
    // unten schickt in den SSO-Redirect, statt dass die API stumm 401 liefert.
    const onExpired = () => {
      void um.getUser().then(async (u) => {
        const neu = u?.refresh_token ? await stillErneuern(um) : null
        setOidcUser(neu)
      })
    }
    um.events.addUserLoaded(onLoaded)
    um.events.addUserUnloaded(onUnloaded)
    um.events.addAccessTokenExpired(onExpired)
    void um.getUser().then(async (u) => {
      // Abgelaufen, aber Refresh-Token da → erst still erneuern, statt den
      // Benutzer sofort in den Login-Redirect zu schicken.
      if (u && u.expired && u.refresh_token) {
        u = await stillErneuern(um)
      }
      setOidcUser(u && !u.expired ? u : null)
      setGeladen(true)
    })
    return () => {
      um.events.removeUserLoaded(onLoaded)
      um.events.removeUserUnloaded(onUnloaded)
      um.events.removeAccessTokenExpired(onExpired)
    }
  }, [])

  // Nicht angemeldet → Login-Redirect (nie auf der Callback-Route und
  // NIE waehrend des Abmeldens — siehe _logoutLaeuft)
  useEffect(() => {
    if (istCallback || !geladen || user || _logoutLaeuft) return
    void getUserManager().signinRedirect()
  }, [istCallback, geladen, user])

  // Angemeldet → Claims fuer die UI-Gates in den Store
  useEffect(() => {
    if (!user) {
      setUser(null)
      return
    }
    const p = user.profile as Record<string, unknown>
    setUser({
      id: String(p.sub ?? ""),
      name: String(p.name ?? p.preferred_username ?? ""),
      email: p.email ? String(p.email) : null,
      roles: rollenAusClaims(p),
    })
  }, [user, setUser])

  if (istCallback) return <>{children}</>
  // Erst rendern, wenn auch der Store den Benutzer kennt (Hans 06.10.2026):
  // der Store wird im Effect oben gesetzt — ohne dieses Gate sehen Seiten
  // beim ersten Zeichnen nach einem Reload noch keinen Benutzer (Übersicht
  // lud dadurch Standardfilter statt der gemerkten).
  if (!user || !storeUser) {
    // Keine vorgeschaltete Anmeldeseite mehr: die gebrandete Authentik-Seite
    // IST die Anmeldeseite (werft-ops#11) — hier nur ein neutraler
    // Zwischenstand, bis der Auto-Redirect (Effect oben) greift.
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-900 text-sm text-slate-400">
        Weiterleitung zur Anmeldung …
      </div>
    )
  }
  return <>{children}</>
}
